import { CanActivateChildFn } from '@angular/router';
import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { map, switchMap, tap } from 'rxjs/operators';
import { getMembershipOfferPriceQuery } from '@aaa/emember/store-membership-offers';
import { filter, of, withLatestFrom } from 'rxjs';
import {
  getIsLoadingPriceOffer,
  getPriceOfferLevelOffers,
  getPriceOfferQuery,
  PriceOffersActions,
  PriceOffersQuery,
} from '@aaa/emember/store-price-offers';
import { ClubApp } from '@aaa/emember/types';
import { environment } from '../../../../environments/environment';
import { DEFAULT_OFFER_PROMO_CODE } from '../../../constants/constants';
import { Pages } from '../../../constants/pages';

export const ensurePriceLoadedGuard: CanActivateChildFn = (_childRoute, state) => {
  const store = inject(Store);
  const load = (query: PriceOffersQuery) =>
    of(query).pipe(
      tap(() => store.dispatch(PriceOffersActions.load({ query }))),
      switchMap(() => store.select(getIsLoadingPriceOffer).pipe(filter((isLoading) => !isLoading))),
      map(() => true),
    );
  const loaded = () => of(true);

  return store.select(getMembershipOfferPriceQuery).pipe(
    switchMap((query) =>
      of(query).pipe(withLatestFrom(store.select(getPriceOfferQuery), store.select(getPriceOfferLevelOffers))),
    ),
    switchMap(([query, stateQuery, levelOffers]) => {
      // Colorado offer pages use the default promo when the URL does not provide one.
      const isJoinPage = state.url.startsWith(Pages.join);
      const isOfferPage = state.url.startsWith(Pages.offers);

      let queryToLoad: PriceOffersQuery = query;

      // Only for Colorado
      if ((isOfferPage || isJoinPage) && environment.clubId === ClubApp.Colorado) {
        const promoCode = query.promoCode ? query.promoCode : DEFAULT_OFFER_PROMO_CODE;

        queryToLoad = { ...query, promoCode };
      }

      // Only compare fields that affect price offer loading.
      const queryChanged =
        queryToLoad.programCode !== stateQuery.programCode ||
        queryToLoad.couponCode !== stateQuery.couponCode ||
        queryToLoad.promoCode !== stateQuery.promoCode;

      // Existing offers can be reused only when they match the effective query.
      const hasLevelOffers = Boolean(levelOffers);
      const stateUsesDefaultPromo = stateQuery.promoCode === DEFAULT_OFFER_PROMO_CODE;

      // If default-promo offers are already shown, refresh them in the background so navigation stays unblocked.
      const shouldLoadWithoutWaiting = hasLevelOffers && queryChanged && stateUsesDefaultPromo;

      if (shouldLoadWithoutWaiting) {
        store.dispatch(PriceOffersActions.load({ query: queryToLoad }));
        return of(true);
      }

      // Otherwise, wait for the load when offers are missing or no longer match the query.
      const shouldLoadAndWait = !hasLevelOffers || queryChanged;

      if (shouldLoadAndWait) {
        // console.log('load price with waiting', queryToLoad.promoCode);
        return load(queryToLoad);
      }

      // console.log('load price from state', queryToLoad.promoCode);
      return loaded();
    }),
  );
};

export default ensurePriceLoadedGuard;
